/**
 * Application route configuration.
 *
 * Uses React Router's lazy loading for code splitting.
 * Each page is loaded on demand. Layouts are used as
 * route elements with nested Outlet children.
 */

import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { ROUTES } from './paths';
import {
  PublicLayout,
  AuthLayout,
  DashboardLayout,
  AdminLayout,
  ErrorLayout,
  WorkspaceLayout,
  ReviewLayout,
} from '@/layouts';
import { AuthGuard, GuestGuard, RoleGuard } from '@/app/auth/guards';

const HomePage = lazy(() => import('@/pages/public/HomePage'));
const AboutPage = lazy(() => import('@/pages/public/AboutPage'));
const StandardsPage = lazy(() => import('@/pages/public/StandardsPage'));
const HowItWorksPage = lazy(() => import('@/pages/public/HowItWorksPage'));
const FAQPage = lazy(() => import('@/pages/public/FAQPage'));
const ResourceCenterPage = lazy(() => import('@/pages/public/ResourceCenterPage'));
const ApplyPage = lazy(() => import('@/pages/public/ApplyPage'));
const ApplicationPage = lazy(() => import('@/pages/public/ApplicationPage'));
const ContactPage = lazy(() => import('@/pages/public/ContactPage'));
const PrivacyPage = lazy(() => import('@/pages/public/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/public/TermsPage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const DashboardProfilePage = lazy(() => import('@/pages/dashboard/DashboardProfilePage'));
const DashboardCommunityPage = lazy(() => import('@/pages/dashboard/DashboardCommunityPage'));
const DashboardEventsPage = lazy(() => import('@/pages/dashboard/DashboardEventsPage'));
const DashboardContentPage = lazy(() => import('@/pages/dashboard/DashboardContentPage'));
const DashboardAnalyticsPage = lazy(() => import('@/pages/dashboard/DashboardAnalyticsPage'));
const DashboardNotificationsPage = lazy(() => import('@/pages/dashboard/DashboardNotificationsPage'));
const DashboardSettingsPage = lazy(() => import('@/pages/dashboard/DashboardSettingsPage'));
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'));
const MonitoringDashboard = lazy(() => import('@/pages/admin/MonitoringDashboard'));
const WorkspaceOverviewPage = lazy(() => import('@/pages/workspace/WorkspaceOverviewPage'));
const WorkspaceProfilePage = lazy(() => import('@/pages/workspace/WorkspaceProfilePage'));
const WorkspaceApplicationPage = lazy(() => import('@/pages/workspace/WorkspaceApplicationPage'));
const WorkspaceStatusPage = lazy(() => import('@/pages/workspace/WorkspaceStatusPage'));
const WorkspaceAccountPage = lazy(() => import('@/pages/workspace/WorkspaceAccountPage'));
const ReviewOverviewPage = lazy(() => import('@/pages/review/ReviewOverviewPage'));
const ReviewQueuePage = lazy(() => import('@/pages/review/ReviewQueuePage'));
const ReviewDetailsPage = lazy(() => import('@/pages/review/ReviewDetailsPage'));
const ReviewHistoryPage = lazy(() => import('@/pages/review/ReviewHistoryPage'));
const ReviewSearchPage = lazy(() => import('@/pages/review/ReviewSearchPage'));
const ReviewSettingsPage = lazy(() => import('@/pages/review/ReviewSettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/error/NotFoundPage'));
const ServerErrorPage = lazy(() => import('@/pages/error/ServerErrorPage'));
const RegistryPage = lazy(() => import('@/pages/registry/RegistryPage'));
const CreatorProfilePage = lazy(() => import('@/pages/registry/CreatorProfilePage'));

export const routes: RouteObject[] = [
  {
    path: ROUTES.HOME,
    element: <PublicLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'standards', element: <StandardsPage /> },
      { path: 'how-it-works', element: <HowItWorksPage /> },
      { path: 'faq', element: <FAQPage /> },
      { path: 'resources', element: <ResourceCenterPage /> },
      { path: 'apply', element: <ApplyPage /> },
      {
        path: 'application',
        element: (
          <AuthGuard>
            <ApplicationPage />
          </AuthGuard>
        ),
      },
      { path: 'contact', element: <ContactPage /> },
      { path: 'registry', element: <RegistryPage /> },
      { path: 'registry/:creatorId', element: <CreatorProfilePage /> },
      { path: 'privacy', element: <PrivacyPage /> },
      { path: 'terms', element: <TermsPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: ROUTES.LOGIN,
    element: (
      <GuestGuard>
        <AuthLayout />
      </GuestGuard>
    ),
    children: [
      { index: true, element: <LoginPage /> },
    ],
  },
  {
    path: ROUTES.REGISTER,
    element: (
      <GuestGuard>
        <AuthLayout />
      </GuestGuard>
    ),
    children: [
      { index: true, element: <RegisterPage /> },
    ],
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: <AuthLayout />,
    children: [
      { index: true, element: <ForgotPasswordPage /> },
    ],
  },
  {
    path: ROUTES.RESET_PASSWORD,
    element: <AuthLayout />,
    children: [
      { index: true, element: <ResetPasswordPage /> },
    ],
  },
  {
    path: ROUTES.VERIFY_EMAIL,
    element: <AuthLayout />,
    children: [
      { index: true, element: <VerifyEmailPage /> },
    ],
  },
  {
    path: ROUTES.DASHBOARD,
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'profile', element: <DashboardProfilePage /> },
      { path: 'community', element: <DashboardCommunityPage /> },
      { path: 'events', element: <DashboardEventsPage /> },
      { path: 'content', element: <DashboardContentPage /> },
      { path: 'analytics', element: <DashboardAnalyticsPage /> },
      { path: 'notifications', element: <DashboardNotificationsPage /> },
      { path: 'settings', element: <DashboardSettingsPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: ROUTES.ADMIN,
    element: (
      <AuthGuard>
        <RoleGuard roles={['admin']}>
          <AdminLayout />
        </RoleGuard>
      </AuthGuard>
    ),
    children: [
      { index: true, element: <AdminPage /> },
      { path: 'monitoring', element: <MonitoringDashboard /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: ROUTES.WORKSPACE,
    element: (
      <AuthGuard>
        <WorkspaceLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <WorkspaceOverviewPage /> },
      { path: 'profile', element: <WorkspaceProfilePage /> },
      { path: 'application', element: <WorkspaceApplicationPage /> },
      { path: 'status', element: <WorkspaceStatusPage /> },
      { path: 'account', element: <WorkspaceAccountPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: ROUTES.REVIEW,
    element: (
      <AuthGuard>
        <RoleGuard roles={['admin']}>
          <ReviewLayout />
        </RoleGuard>
      </AuthGuard>
    ),
    children: [
      { index: true, element: <ReviewOverviewPage /> },
      { path: 'queue', element: <ReviewQueuePage /> },
      { path: 'applications/:applicationId', element: <ReviewDetailsPage /> },
      { path: 'history', element: <ReviewHistoryPage /> },
      { path: 'search', element: <ReviewSearchPage /> },
      { path: 'settings', element: <ReviewSettingsPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: '/error',
    element: <ErrorLayout />,
    children: [
      { index: true, element: <ServerErrorPage /> },
    ],
  },
  {
    path: ROUTES.NOT_FOUND,
    element: <ErrorLayout />,
    children: [
      { index: true, element: <NotFoundPage /> },
    ],
  },
];
